<script
  setup
  lang="ts"
  generic="
    ListingFilter extends {
      id: string;
      code: keyof Schemas['ProductListingResult']['currentFilters'];
      label: string;
      name: string;
    }
  "
>
import type { Schemas } from "#shopware";
import { onClickOutside } from "@vueuse/core";
import { computed, ref } from "vue";

const props = defineProps<{
  filter: ListingFilter;
  selectedFilters: Schemas["ProductListingResult"]["currentFilters"];
}>();

const emits = defineEmits<{
  (e: "select-value", value: { code: string; value: unknown }): void;
}>();
const currentFilterData = computed(
  () => !!props.selectedFilters[props.filter?.code]
);
const onChangeOption = (): void => {
  emits("select-value", {
    code: props.filter?.code,
    value: !currentFilterData.value,
  });
};

const isFilterVisible = ref<boolean>(true);
const toggle = () => {
  isFilterVisible.value = !isFilterVisible.value;
};

const dropdownElement = ref(null);
onClickOutside(dropdownElement, () => (isFilterVisible.value = false));

if (currentFilterData.value) {
  toggle();
}
</script>

<template>
  <div class="filter-shippingfree-element">
    <button
      class="accordion-header w-full text-left py-4 font-bold flex justify-between items-center cursor-pointer bg-transparent"
      @click="toggle"
    >
      <span class="text-base font-bold text-dark-bg">{{
        props.filter.label
      }}</span>
      <svg
        id="icon-price"
        xmlns="http://www.w3.org/2000/svg"
        class="h-[16px] w-[16px] transform transition-transform duration-200"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        :class="[!isFilterVisible ? 'rotate-0' : 'rotate-180']"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>

    <transition name="fade" mode="out-in">
      <div class="accordion-body" :class="[!isFilterVisible ? 'hidden' : '']">
        <ul class="space-y-2 mb-4">
          <li class="mb-2 text-sm font-normal">
            <div
              class="flex items-center justify-start"
              @click="onChangeOption()"
            >
              <label
                class="relative flex cursor-pointer items-center"
                :for="`filter-mobile-${props.filter.id || props.filter.code}`"
                data-ripple-dark="true"
              >
                <input
                  :id="`filter-mobile-${props.filter.id || props.filter.code}`"
                  :checked="currentFilterData"
                  :name="props.filter.name"
                  :value="props.filter.name"
                  type="checkbox"
                  class="peer relative h-4 w-4 cursor-pointer appearance-none border border-custom-grey transition-all checked:border-dark-bg checked:bg-dark-bg checked:before:bg-dark-bg"
                />
                <span
                  class="pointer-events-none absolute top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 text-white opacity-0 transition-opacity peer-checked:opacity-100"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-3.5 w-3.5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    stroke="currentColor"
                    stroke-width="1"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    ></path>
                  </svg>
                </span>
              </label>
              <label
                class="cursor-pointer text-dark-bg text-sm ml-3 truncate max-w-[130px]"
                :for="`filter-mobile-${props.filter.id || props.filter.code}`"
                >{{ props.filter.label }}</label
              >
            </div>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
